import { Stack, Typography } from "@mui/material";
import { type MouseEventHandler, useState } from "react";
import { type RowClickFunction, CreateButton } from "react-admin";
import { type SubmitHandler } from "react-hook-form";

import { MODULE_NAMES } from "@/constants/modules";

import {
  type CampaignTdsRecordsToCreate,
  CampaignTdsCreateFormModal,
} from "./CampaignTdsCreateFormModal";
import { CampaignTdsDataGrid } from "./CampaignTdsDataGrid";
import {
  type CampaignTdsRecordToEdit,
  CampaignTdsEditFormModal,
} from "./CampaignTdsEditFormModal";

export type CampaignTdsRecord = {
  id: number;
  landing: string;
  countrySet: string[];
  modelSet: string[];
  osSet: string[];
};

type Props = {
  data: CampaignTdsRecord[] | undefined;
  isDisabled?: boolean;
  onEditCampaign: (record: CampaignTdsRecordToEdit) => void;
  onCreateCampaigns: (records: CampaignTdsRecordsToCreate) => void;
};

export const CampaignTdsCreate = ({
  data,
  isDisabled,
  onEditCampaign,
  onCreateCampaigns,
}: Props) => {
  const [campaignToEdit, setCampaignToEdit] =
    useState<CampaignTdsRecordToEdit | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleClickRow: RowClickFunction = (_index, _resource, record) => {
    setCampaignToEdit(record as CampaignTdsRecordToEdit);

    return false;
  };

  const handleCloseEditModal = () => {
    setCampaignToEdit(null);
  };

  const handleEditTds: SubmitHandler<CampaignTdsRecordToEdit> = (values) => {
    onEditCampaign(values);
    setCampaignToEdit(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateTds: SubmitHandler<CampaignTdsRecordsToCreate> = (
    values,
  ) => {
    onCreateCampaigns(values);
    setIsCreateModalOpen(false);
  };

  const handleOpenCreateDialog: MouseEventHandler<HTMLAnchorElement> = (
    event,
  ) => {
    event.preventDefault();

    setIsCreateModalOpen(true);
  };

  return (
    <Stack direction="column" spacing={2} width="100%" marginTop="20px">
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="h3" fontSize="16px">
          Campaign TDS
        </Typography>
        <CreateButton
          type="button"
          disabled={isDisabled}
          resource={MODULE_NAMES.CAMPAIGN_TDS}
          onClick={handleOpenCreateDialog}
        />
      </Stack>
      <CampaignTdsDataGrid
        isPending={isDisabled}
        data={data}
        onClickRow={handleClickRow}
      />
      <CampaignTdsEditFormModal
        isOpen={campaignToEdit !== null}
        record={campaignToEdit}
        onClose={handleCloseEditModal}
        onSubmit={handleEditTds}
      />
      <CampaignTdsCreateFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseCreateModal}
        onSubmit={handleCreateTds}
      />
    </Stack>
  );
};
